import React from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, RadialBarChart, RadialBar,
  PieChart, Pie, Cell, Tooltip
} from 'recharts';
import { Activity, CheckCircle2, CircleDashed, ShieldCheck } from 'lucide-react';

const STATUS_COLORS = ['#10b981', '#f59e0b', '#ef4444'];

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 px-3 py-2 text-xs text-slate-200 shadow-xl" data-testid="compliance-chart-tooltip">
      <span className="text-slate-400">{payload[0].name}: </span>
      <strong>{payload[0].value}</strong>
    </div>
  );
};

const MaturityOverview = ({ overallScore, progressPercentage, stagesCompleted, statusCounts, scoreMessage }) => {
  const radialData = [{ name: 'Maturity score', value: Math.max(overallScore, 1), fill: overallScore ? '#10b981' : '#334155' }];
  const statusData = [
    { name: 'Good', value: statusCounts.good || 0 },
    { name: 'Attention', value: statusCounts.attention || 0 },
    { name: 'Critical', value: statusCounts.critical || 0 },
  ].filter((item) => item.value > 0);
  const totalControls = statusData.reduce((sum, item) => sum + item.value, 0);

  return (
    <motion.section
      className="dashboard-glass relative mb-6 overflow-hidden rounded-2xl p-5 sm:p-7"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      data-testid="maturity-overview-panel"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/70 to-transparent" />
      <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-4">
          <p className="dashboard-eyebrow mb-2">Maturity score</p>
          <div className="relative mx-auto aspect-square w-full max-w-[250px]" data-testid="maturity-score-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 250, height: 250 }}>
              <RadialBarChart innerRadius="76%" outerRadius="100%" barSize={14} data={radialData} startAngle={210} endAngle={-30}>
                <RadialBar dataKey="value" background={{ fill: '#1e293b' }} cornerRadius={8} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-2 text-center">
              <span className="font-heading text-5xl font-semibold text-white" data-testid="maturity-score-value">{Math.round(overallScore)}%</span>
              <span className="mt-1 text-xs text-slate-400">overall readiness</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="flex items-start gap-3">
            <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-emerald-400/25 bg-emerald-400/10">
              <Activity className="h-5 w-5 text-emerald-300" />
            </div>
            <div>
              <p className="dashboard-eyebrow mb-2">Assessment completion</p>
              <h2 className="font-heading text-2xl font-medium text-white" data-testid="assessment-progress-value">{progressPercentage}% complete</h2>
              <p className="mt-2 text-sm leading-6 text-slate-400" data-testid="assessment-progress-message">{scoreMessage}</p>
            </div>
          </div>

          <div className="mt-7" data-testid="assessment-stage-progress">
            <div className="mb-3 flex items-center justify-between text-xs text-slate-400">
              <span>Assessment stages</span>
              <span>{stagesCompleted} of 4 complete</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((stage) => {
                const complete = stage <= stagesCompleted;
                return (
                  <div key={stage}>
                    <div className={`h-2 rounded-full ${complete ? 'bg-emerald-400 shadow-[0_0_14px_rgba(16,185,129,0.35)]' : 'bg-slate-800'}`} />
                    <div className={`mt-2 flex items-center gap-1 text-[11px] ${complete ? 'text-emerald-300' : 'text-slate-600'}`}>
                      {complete ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />}
                      <span className="hidden sm:inline">Stage {stage}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-7 lg:col-span-3 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
          <p className="dashboard-eyebrow mb-3">Control health</p>
          {totalControls > 0 ? (
            <>
              <div className="relative mx-auto h-40 w-full max-w-[220px]" data-testid="compliance-status-chart">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 220, height: 160 }}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={66} paddingAngle={4} stroke="none">
                      {statusData.map((entry) => <Cell key={entry.name} fill={STATUS_COLORS[['Good', 'Attention', 'Critical'].indexOf(entry.name)]} />)}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-heading text-2xl font-semibold text-white">{totalControls}</span>
                  <span className="text-[10px] uppercase text-slate-500">Controls</span>
                </div>
              </div>
              <div className="mt-2 space-y-2" data-testid="compliance-status-legend">
                {statusData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-slate-400"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[['Good', 'Attention', 'Critical'].indexOf(item.name)] }} />{item.name}</span>
                    <span className="font-mono text-slate-200">{item.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex min-h-48 flex-col items-center justify-center text-center" data-testid="compliance-status-empty-state">
              <ShieldCheck className="mb-3 h-9 w-9 text-slate-600" />
              <p className="text-sm leading-6 text-slate-500">Control health appears as you complete assessment stages.</p>
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default MaturityOverview;